import { generateClient } from "aws-amplify/api";
import i18n from "i18n";
import store from "store";
import { refreshAccessTokenMutation } from "api/auth/refreshAccessToken";

const TWIIK_CLIENT_ID = process.env.REACT_APP_TWIIK_CLIENT_ID;

const getPropByPath = (object, path, defaultValue) => {
  const _path = Array.isArray(path) ? path : path.split(".");
  if (object && _path.length) {
    return getPropByPath(object[_path.shift()], _path, defaultValue);
  }
  return object === undefined ? defaultValue : object;
};

const client = generateClient();

const isUnauthorizedError = (response) =>
  Boolean(
    response?.errors?.some?.((e) => e?.errorType === "UnauthorizedException")
  );

let refreshPromise = null;

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = store.getState().auth.refreshToken;
    if (!refreshToken) {
      store.dispatch({ type: "LOGOUT" });
      throw new Error("No refresh token available");
    }
    try {
      const result = await doRequest(
        {
          query: refreshAccessTokenMutation,
          variables: { refreshToken },
        },
        "data.refreshAccessToken",
        true
      );
      if (!result?.accessToken) {
        throw new Error("Refresh did not return an access token");
      }
      store.dispatch({
        type: "LOGIN",
        payload: {
          logged: true,
          accessToken: result.accessToken,
          refreshToken: result.refreshToken || refreshToken,
          expireTime: result.expireTime,
          tokenType: result.tokenType,
        },
      });
      return result.accessToken;
    } catch (e) {
      store.dispatch({ type: "LOGOUT" });
      throw e;
    }
  })();

  refreshPromise.finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

export default async function doRequest(
  requestMeta,
  successPath,
  useBasicAuth = false,
  retryOnAuth = true
) {
  const language = i18n.language || "en";
  let result;
  try {
    if (useBasicAuth) {
      const clientId = `${TWIIK_CLIENT_ID}`;
      result = await client.graphql(requestMeta, {
        Authorization: `APIKEY ${clientId}`,
        "Accept-Language": language,
      });
    } else {
      const token = store.getState().auth.accessToken;
      result = await client.graphql(requestMeta, {
        Authorization: `BEARER ${token}`,
        "Accept-Language": language,
      });
    }
  } catch (error) {
    if (error?.data) {
      result = error;
    } else {
      throw error;
    }
  }

  if (!useBasicAuth && retryOnAuth && isUnauthorizedError(result)) {
    try {
      await refreshAccessToken();
      return doRequest(requestMeta, successPath, useBasicAuth, false);
    } catch (_e) {
      // Refresh failed; LOGOUT was already dispatched. Fall through and let
      // the original auth error propagate to the caller as a normal failure.
    }
  }

  if (successPath) {
    const data = getPropByPath(result, successPath);
    if (data?.errorMessage) {
      throw data.errorMessage;
    }
    if (data == null && result?.errors?.length) {
      throw result;
    }
    return data;
  } else if (result.errorMessage) {
    throw result.errorMessage;
  }
  return result;
}
